/**
 * Columns for the PO-line drill-downs (PO Lines, OTIF, Arrears, Qty Diff —
 * one format, review items 11-14). Customer review 11 Sep.
 *
 * "Last Received Date" is the latest GRN against the line (a line can take
 * several receipts), computed for the page in one grouped Max query
 * (`last_received_date`, see POLineListingMixin._enrich_page_aggregates) —
 * never per row. It replaces the old "Delivery Date" column (msg 11b).
 */
import { Badge } from '../../../components/ui/badge';
import PoHeaderLink from '../../../components/common/PoHeaderLink';
import PoLineLink from '../../../components/common/PoLineLink';
import { PurchaseOrderLine } from '../../../lib/api';
import {
  OtifDecisionBadge, OTIF_DECISION_LABEL, otifDecisionOf,
} from '../../../components/purchasing/otifDecision';
import { DrilldownColumn, DrilldownFormatters, dateOf, isFrozenRow, num, yesNo } from './columns';
import { statusOf } from '../../../components/purchasing/lineStatus';
import { getOTIFStatusStyle } from '../../../components/drilldown/drilldownUtils';
// CR-7412: the PO-line receipt vocabulary, from its one owner.
import {
  CLOSED_RECEIPT_STATUSES, RECEIPT_STATUS_LABELS,
} from '../../../components/purchasing/receiptStatus';
import { ColumnOptions } from './columnsFor';
import {
  CUSTOMER_REQUIRED_DATE, MRP_SCHEDULED_DATE, ORIGINAL_REQUIRED_DATE, SUPPLIER_PROMISE_DATE,
} from '../../../config/dateLabels';

/**
 * The line's OTIF verdict. CR-7270: a line with no verdict reads "Pending",
 * the same word the dashboard's OTIF modal and the PO Line dashboard drill
 * use — one column, one vocabulary, whichever screen the customer opened it
 * from. It is never the line's lifecycle: that is the Status column beside it,
 * which says pending / partially_received / received in its own words.
 *
 * CR-7416 (19 Sep 2026): "one vocabulary" was the claim and it was FALSE. This
 * was a second implementation of the rule in `getOTIFStatusStyle`, and the two
 * had already drifted apart on the words — this one printed "Pass" / "Fail"
 * where the dashboard's printed "PASS" / "FAILED", so one line read two
 * different ways on two screens showing the same verdict. It asks the shared
 * function now; the rule about a line reaching no verdict until it closes lives
 * there, once.
 */
function otifVerdict(v: boolean | null | undefined, lineStatus?: string | null): string {
  return getOTIFStatusStyle(v, lineStatus).label;
}

/**
 * The word the Status column shows: the live badge, or a frozen row's captured
 * lifecycle, or the one mark for any null (CR-7416).
 *
 * A live row with neither an acknowledge status nor a hold reads '-' rather
 * than the "New" `statusOf` would default it to — that would be a claim on a
 * row that made none.
 */
function statusWord(l: PurchaseOrderLine): string {
  // CR-7412's vocabulary on the captured word: the raw column value was
  // reaching the screen, which is the defect that ticket was raised for.
  if (isFrozenRow(l)) {
    return l.status ? (RECEIPT_STATUS_LABELS[l.status] ?? l.status) : '-';
  }
  if (!l.acknowledge_status && !l.on_hold) return '-';
  return statusOf(l).label;
}

/** Ordered − received: the line's term of the (net) Qty Diff cell. */
function qtyDiff(l: PurchaseOrderLine): number | null {
  const ordered = num(l.quantity);
  const received = num(l.received_qty || '0');
  return ordered === null || received === null ? null : Math.round((ordered - received) * 1000) / 1000;
}

export function poLineColumns(f: DrilldownFormatters, options: ColumnOptions): DrilldownColumn<PurchaseOrderLine>[] {
  const customerView = !options.isSupplier;
  const diff: DrilldownColumn<PurchaseOrderLine>[] = options.metric === 'qty_diff' ? [{
    key: 'qty_diff', label: 'Qty Diff', kind: 'qty', align: 'right', className: 'font-medium',
    title: 'Qty Ordered − Qty Received. The column sums to the Qty Diff cell (a net figure: over- and under-deliveries offset)',
    value: (l) => qtyDiff(l),
  }] : [];

  return [
    // CR-1154: PO number links to the PO header; line to the line item.
    {
      key: 'po_number', label: 'PO Number', className: 'font-medium',
      value: (l) => l.po_number ?? null,
      render: (l) => <PoHeaderLink poLineId={l.id} poNumber={l.po_number}>{l.po_number || '-'}</PoHeaderLink>,
    },
    {
      key: 'line_number', label: 'Line #',
      value: (l) => l.line_number,
      render: (l) => <PoLineLink poLineId={l.id}>{l.line_number}</PoLineLink>,
    },
    // The PO's own customer site (review item 3: PRO0005371 is BENGALURU).
    { key: 'customer_name', label: 'Customer', value: (l) => l.customer_name ?? null },
    { key: 'supplier_group_name', label: 'Supplier Group', value: (l) => l.supplier_group_name ?? null },
    { key: 'supplier_name', label: 'Supplier Site', value: (l) => l.supplier_name ?? null },
    { key: 'part_number', label: 'Part Number', value: (l) => l.part_number },
    { key: 'part_description', label: 'Part Description', truncate: true, value: (l) => l.part_description ?? null },
    // CR-7270: `?? null` rather than `|| '0'`. On a frozen row both are the
    // CAPTURED quantities, and a row captured before the snapshot held them
    // sends null, which the cell reads as N/A. Coercing null to '0' would
    // state "nothing was received that month", which is a claim the capture
    // cannot support. Live rows are unaffected: both columns are non-nullable
    // on the model.
    { key: 'quantity', label: 'Qty Ordered', kind: 'qty', align: 'right', value: (l) => l.quantity ?? null },
    { key: 'received_qty', label: 'Qty Received', kind: 'qty', align: 'right', className: 'text-green-600', value: (l) => l.received_qty ?? null },
    ...diff,
    { key: 'outstanding_qty', label: 'Outstanding', kind: 'qty', align: 'right', className: 'text-orange-600', value: (l) => l.outstanding_qty || '0' },
    { key: 'unit_price', label: 'Unit Price', kind: 'money', align: 'right', value: (l) => l.unit_price, currency: (l) => l.currency },
    ...(customerView ? [{
      key: 'converted_unit_price', label: 'Converted Unit Price', kind: 'money', align: 'right',
      title: "Unit price in the customer's reporting currency",
      value: (l: PurchaseOrderLine) => l.converted_unit_price ?? null,
      currency: (l: PurchaseOrderLine) => l.converted_currency,
    } as DrilldownColumn<PurchaseOrderLine>] : []),
    { key: 'currency', label: 'Currency', value: (l) => l.currency ?? null },
    // DATE-FIELD-CANON PR 2: the Customer Required Date (the date expediting
    // moves) was never on this drill-down; the promise date carried a
    // non-canon label (Review Date LSP, now retired).
    {
      key: 'delivery_date', label: CUSTOMER_REQUIRED_DATE,
      title: 'Customer Required Date — the date the customer currently needs the line by',
      value: (l) => dateOf(f.formatDate, l.customer_required_date),
    },
    { key: 'mrp_scheduled_date', label: MRP_SCHEDULED_DATE, value: (l) => dateOf(f.formatDate, l.mrp_scheduled_date) },
    {
      key: 'supplier_promise_date', label: SUPPLIER_PROMISE_DATE,
      title: 'Latest supplier promise date',
      value: (l) => dateOf(f.formatDate, l.supplier_promise_date),
    },
    // ODD = the server's odd_date — the value the PO Lines grid's Original
    // Required Date (ODD) column and the line pop-up show, and the date OTIF measures against.
    { key: 'odd', label: ORIGINAL_REQUIRED_DATE, value: (l) => dateOf(f.formatDate, l.odd_date ?? l.customer_required_date) },
    {
      key: 'last_received_date', label: 'Last Received Date',
      title: 'Date of the latest GRN received against this line',
      value: (l) => dateOf(f.formatDate, l.last_received_date),
    },
    // CR-7270: both read the line's lifecycle status. On a frozen row that is
    // the CAPTURED status (pending / partially_received), or null on a row
    // captured before the snapshot held it — and `l.status === 'received'`
    // would answer null with a confident 'No'.
    // CR-7412 item 10: the word "Completed" is retired. It was OUR word for
    // the ERP's flag and it was computed two different ways under one label —
    // here from the line's status, and in the backend registry's `is_complete`
    // from `received_qty >= quantity`, which disagree on exactly the lines that
    // matter (a line the feed reports complete without full receipt: 9,167 of
    // them on atlascopco-staging). Both now say Closed, and both answer from
    // the status. The null guard below is CR-7270's and is unchanged.
    { key: 'completed', label: 'Closed', align: 'center', value: (l) => yesNo(l.status == null ? null : CLOSED_RECEIPT_STATUSES.includes(l.status)) },
    {
      // CR-7412 item 9: this printed the RAW database value — "received",
      // "pending" — as a user-facing status, which is the same fault CR-7305
      // was raised for on the PO Details Lines tab. It reads the shared label
      // map now, so a finished line says Closed.
      key: 'status', label: 'Status', align: 'center',
      // CR-7416 (Aaron, 19 Sep): on a LIVE row this is the badge every other
      // PO-line surface prints, from the one function that decides it, instead
      // of the raw lifecycle code. `statusOf` answers the hold first, so a held
      // line reads "On Hold (Customer)" here exactly as it does in the grid and
      // the modal.
      //
      // EXCEPT ON A FROZEN ROW, and this is the whole of CR-7270's rule applied
      // to a new column. A row described AS AT a month end is rendered from the
      // capture, and the capture holds the LIFECYCLE and no acknowledge status
      // at all. Reading the badge there would either print today's status under
      // a "cached month-end data" banner, or — since the field is simply absent
      // — turn every arrears row into a dash and lose what the month did know.
      //
      // And on that frozen row the captured word is HUMANISED through CR-7412's
      // vocabulary, which is the other half of this column's history: it landed
      // while this branch was open and exists because the raw database value was
      // reaching a human ("pending", "new" on a closed line). The two rulings do
      // not actually collide — CR-7416 decides what a LIVE row shows, CR-7412
      // decides what the CAPTURED word reads like — and each ticket's own test
      // passes here unchanged, which is the evidence that this is the reading
      // and not a compromise. The lifecycle is not lost either way: the Closed
      // column above still derives from it.
      value: (l) => statusWord(l),
      render: (l) => {
        const word = statusWord(l);
        if (word === '-') return <span className="text-muted-foreground">-</span>;
        if (isFrozenRow(l)) {
          // The captured lifecycle, in its own words, marked as not-live by the
          // outline treatment rather than dressed up as a workflow badge.
          return <Badge variant="outline">{word}</Badge>;
        }
        const { className, title } = statusOf(l);
        return <Badge className={className} title={title}>{word}</Badge>;
      },
    },
    {
      key: 'is_otif', label: 'OTIF', align: 'center',
      title: "The line's OTIF verdict (blank until the line is closed and judged)",
      value: (l) => otifVerdict(l.is_otif, l.status),
      // CR-7416 (Aaron, 19 Sep), reversing his 15/18 Sep ruling that an absent
      // verdict be plain text: it read as a broken cell beside the two coloured
      // ones. All three are badges now and the hierarchy is the colour, which
      // `getOTIFStatusStyle` owns along with the word — so a wording change
      // cannot leave a green "FAILED" behind, which is what the
      // `verdict === 'Pass'` string match this replaced would have done the
      // moment "Pass" became "PASSED".
      render: (l) => {
        const { className, label } = getOTIFStatusStyle(l.is_otif, l.status);
        return <Badge className={className}>{label}</Badge>;
      },
    },

    // CR-7418 (Aaron, 19 Sep): ONE column, called OTIF Review, carrying the
    // status. It replaces the pair that stood here — an 'OTIF Acknowledged'
    // yes/no beside an 'Overridden' yes/no, which between them printed "No"
    // and "No" on a line nobody had looked at and made the reader work out
    // that this meant undecided.
    //
    // Shown to a supplier too, read only (CR-6348, Aaron 5 Oct 2026, reversing
    // the 20 Sep restriction): the verdict and the buyer's review of it alike.
    //
    // A line with no review to report yields undefined, and the cell is blank
    // on screen and in the file alike — `review_status` answers the same way
    // server-side, so neither surface invents a "Not Decided" the other does
    // not show.
    {
      key: 'otif_review', label: 'OTIF Review', align: 'center',
      value: (l: PurchaseOrderLine) => {
        const d = otifDecisionOf(l);
        return d ? OTIF_DECISION_LABEL[d] : '';
      },
      render: (l: PurchaseOrderLine) => <OtifDecisionBadge line={l} />,
    },
    { key: 'updated_at', label: 'Update Time', value: (l) => dateOf(f.formatDateTime, l.updated_at) },
  ];
}
